import axiosInstance from '../axios';

export interface ChannelUserDto {
  id: string;
  channel_id: string;
  user_id: string;
  is_read: number;
  user: {
    id: string;
    first_name: string;
    last_name: string;
    profile_image: string;
    provider?: {
      company_name?: string;
    };
  };
}

export interface ChannelListDto {
  id: string;
  reference_id: string | null;
  reference_type: string | null;
  created_at: string;
  updated_at: string;
  channel_users: ChannelUserDto[];
  last_message_sent_user?: string;
  last_sent_message?: string;
  last_sent_attachment_type?: string;
  last_sent_files_count?: number;
  is_read?: number;
}

export interface ConversationFileDto {
  id: string;
  conversation_id: string;
  original_file_name: string;
  stored_file_name: string;
  file_type: string;
  file_path?: string;
}

export interface ConversationDto {
  id: string;
  channel_id: string;
  user_id: string;
  message: string;
  created_at: string;
  user: {
    id: string;
    first_name: string;
    last_name: string;
    profile_image: string;
  };
  conversation_files?: ConversationFileDto[];
}

export interface PaginatedChatResponse<T> {
  current_page: number;
  data: T[];
  last_page: number;
  total: number;
}

export interface ApiChatEnvelope<T> {
  response_code: string;
  message: string;
  content: T;
  errors?: unknown;
}

export const chatService = {
  getChannelList: async (params?: { limit?: number; offset?: number }) => {
    const { data } = await axiosInstance.get<ApiChatEnvelope<PaginatedChatResponse<ChannelListDto>>>(
      '/v1/chat/channel-list',
      {
        params: {
          limit: params?.limit || 100,
          offset: params?.offset || 1,
        },
      }
    );
    return data.content.data;
  },

  getConversation: async (channelId: string, params?: { limit?: number; offset?: number }) => {
    const { data } = await axiosInstance.get<ApiChatEnvelope<PaginatedChatResponse<ConversationDto>>>(
      '/v1/chat/conversation',
      {
        params: {
          channel_id: channelId,
          limit: params?.limit || 100,
          offset: params?.offset || 1,
        },
      }
    );
    // Reverse data to display oldest top, newest bottom (since backend returns latest())
    return data.content.data.reverse();
  },

  sendMessage: async (payload: { channel_id: string; message: string; reply_to_id?: string; files?: File[] }) => {
    const formData = new FormData();
    formData.append('channel_id', payload.channel_id);
    formData.append('message', payload.message);

    if (payload.reply_to_id) {
      formData.append('reply_to_id', payload.reply_to_id);
    }

    if (payload.files?.length) {
      payload.files.forEach((file) => {
        formData.append('files[]', file);
      });
    }

    const { data } = await axiosInstance.post<ApiChatEnvelope<any>>(
      '/v1/chat/send-message',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return data.content;
  },

  createChannel: async (payload: { to_user: string; reference_id?: string; reference_type?: string }) => {
    const { data } = await axiosInstance.post<ApiChatEnvelope<ChannelListDto>>(
      '/v1/chat/create-channel',
      payload
    );
    return data.content;
  },

  toggleReaction: async (payload: { channel_id: string; message_id: string; emoji: string }) => {
    const { data } = await axiosInstance.post(
      '/v1/chat/reaction-toggle',
      payload
    );
    return data;
  },

  deleteMessage: async (messageId: string) => {
    const { data } = await axiosInstance.delete(
      `/v1/chat/conversation/destroy/${messageId}`
    );
    return data;
  },
};
